import { useState } from 'react';
import {
  deleteUser,
  sendPasswordResetEmail,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  EmailAuthProvider,
  GoogleAuthProvider,
} from 'firebase/auth';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { signOutUser } from '@/lib/signOut';
import { useAuth } from '@/stores/auth';
import { useTheme } from '@/stores/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { toast } from '@/components/ui/Toast';
import { callExportMyData } from '@/lib/callables';
import { SellerSafetyForm } from '@/components/seller/SellerSafetyForm';
import {
  defaultSellerSafetyPreferences,
  type SellerSafetyPreferences,
} from '@makhe/shared';
import type { Theme } from '@/lib/theme';

export default function AccountSettingsPage() {
  const { firebaseUser, userDoc, isSeller } = useAuth();
  const theme = useTheme((s) => s.theme);
  const setTheme = useTheme((s) => s.setTheme);
  const [busy, setBusy] = useState(false);
  const [reauthOpen, setReauthOpen] = useState(false);
  const [reauthPassword, setReauthPassword] = useState('');
  const [safetyPrefs, setSafetyPrefs] = useState<SellerSafetyPreferences>(
    userDoc?.sellerProfile?.safetyPreferences ?? defaultSellerSafetyPreferences(),
  );

  const saveSafetyPrefs = async () => {
    if (!firebaseUser) return;
    setBusy(true);
    try {
      await updateDoc(doc(db, 'users', firebaseUser.uid), {
        'sellerProfile.safetyPreferences': safetyPrefs,
        updatedAt: serverTimestamp(),
      });
      toast.success('Safety preferences saved.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const exportData = async () => {
    setBusy(true);
    try {
      const res = await callExportMyData();
      const blob = new Blob([JSON.stringify(res.data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `makhe-data-${firebaseUser?.uid}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Download started.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const requestPasswordReset = async () => {
    if (!firebaseUser?.email) return;
    try {
      await sendPasswordResetEmail(auth, firebaseUser.email);
      toast.success('Reset email sent.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  // POPIA erasure. Firebase requires a recent login to delete an account; if the
  // session is stale we re-authenticate (Google via popup, email/password via a
  // password prompt) and retry, so the right is actually exercisable.
  const finalizeDelete = async () => {
    if (!firebaseUser) return;
    await deleteUser(firebaseUser);
    toast.success('Account deleted.');
    await signOutUser();
  };

  const deleteAccount = async () => {
    if (!firebaseUser) return;
    if (!confirm('Delete your account? This cannot be undone.')) return;
    setBusy(true);
    try {
      await finalizeDelete();
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code === 'auth/requires-recent-login') {
        const providerId = firebaseUser.providerData[0]?.providerId;
        if (providerId === 'google.com') {
          try {
            await reauthenticateWithPopup(firebaseUser, new GoogleAuthProvider());
            await finalizeDelete();
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Could not re-authenticate.');
          }
        } else {
          // Email/password user — collect the password to re-authenticate.
          setReauthOpen(true);
        }
      } else {
        toast.error(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const confirmReauthAndDelete = async () => {
    if (!firebaseUser?.email) return;
    if (!reauthPassword) {
      toast.error('Enter your password.');
      return;
    }
    setBusy(true);
    try {
      const cred = EmailAuthProvider.credential(firebaseUser.email, reauthPassword);
      await reauthenticateWithCredential(firebaseUser, cred);
      setReauthOpen(false);
      setReauthPassword('');
      await finalizeDelete();
    } catch (err) {
      const code = (err as { code?: string }).code;
      toast.error(
        code === 'auth/wrong-password' || code === 'auth/invalid-credential'
          ? 'Incorrect password.'
          : err instanceof Error
            ? err.message
            : String(err),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mx-auto max-w-2xl px-6 py-6">
      <h1 className="font-display text-3xl">Account</h1>

      <Card className="mt-6 space-y-3">
        <h2 className="font-display text-lg">Appearance</h2>
        <p className="text-sm text-makhe-ink/70">
          Choose light or dark. Dark is the default Makhe look.
        </p>
        <SegmentedControl<Theme>
          value={theme}
          options={[
            { value: 'light', label: 'Light' },
            { value: 'dark', label: 'Dark' },
          ]}
          onChange={setTheme}
        />
      </Card>

      <Card className="mt-4 space-y-3">
        <h2 className="font-display text-lg">Email</h2>
        <p className="text-sm text-makhe-ink/70">{firebaseUser?.email}</p>
        <Button onClick={requestPasswordReset} variant="secondary" size="sm">
          Send password reset
        </Button>
      </Card>

      {isSeller() && (
        <Card className="mt-4 space-y-3">
          <h2 className="font-display text-lg">Seller safety</h2>
          <SellerSafetyForm value={safetyPrefs} onChange={setSafetyPrefs} />
          <Button onClick={saveSafetyPrefs} loading={busy}>
            Save safety preferences
          </Button>
        </Card>
      )}

      <Card className="mt-4 space-y-3">
        <h2 className="font-display text-lg">Your data (POPIA)</h2>
        <p className="text-sm text-makhe-ink/70">
          Download a JSON copy of everything we have on you.
        </p>
        <Button onClick={exportData} loading={busy}>
          Export my data
        </Button>
      </Card>

      <Card className="mt-4 space-y-3 border-makhe-plum/40 bg-makhe-plum/10">
        <h2 className="font-display text-lg text-makhe-plum">Delete account</h2>
        <p className="text-sm text-makhe-ink/70">
          Permanently anonymises your account. Booking and transaction records are retained for 7
          years per SA tax law.
        </p>
        <Button onClick={deleteAccount} variant="danger" loading={busy}>
          Delete account
        </Button>
      </Card>

      <Modal open={reauthOpen} onClose={() => setReauthOpen(false)} title="Confirm it's you">
        <p className="text-sm text-makhe-ink/80">
          For your security, re-enter your password to permanently delete your account.
        </p>
        <div className="mt-4">
          <Input
            label="Password"
            type="password"
            autoComplete="current-password"
            value={reauthPassword}
            onChange={(e) => setReauthPassword(e.target.value)}
          />
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setReauthOpen(false)}>
            Cancel
          </Button>
          <Button variant="danger" loading={busy} onClick={confirmReauthAndDelete}>
            Delete account
          </Button>
        </div>
      </Modal>
    </section>
  );
}
